/**
 * Les dates, dites comme on les lit.
 *
 * Écrit dans `parent/effort` d'abord, sorti ici le 3 septembre 2026 quand le
 * journal de l'enfant a eu besoin de la même phrase. Une coquille enfant qui
 * importerait un module `parent/` pour une date serait un lien de trop entre
 * deux moitiés qu'on tient séparées ; en recopier une seconde version aurait
 * donné deux formats de date dans la même application.
 */

/**
 * Un jour, tel qu'on le lit dans une phrase française.
 *
 * **Deux formes entrent, et elles ne sont pas la même chose.** Un `YYYY-MM-DD`
 * est un jour du calendrier, sans fuseau : il se rend tel quel, et le décaler
 * d'une heure le ferait changer de date. Un horodatage (`2026-09-24T05:34:…Z`)
 * est un instant : le jour qu'il faut en lire est celui de la maison, donc
 * l'instant est rendu dans le fuseau du navigateur. Couper les dix premiers
 * caractères d'un horodatage donnerait le jour **UTC** — à une heure du matin à
 * Paris, la veille.
 *
 * La distinction était tenue par les appelants, et mal : deux écrans écrivaient
 * `enClair(x.slice(0, 10))` à la main, un troisième — « Ce qui a été préparé »,
 * le 24 septembre 2026 — l'a oublié et affichait l'horodatage brut au parent.
 * C'est la forme que ce dépôt paie depuis vingt occurrences, sous sa variante de
 * lecture : la conversion est ici, une fois, et plus personne ne la recopie.
 */
export function enClair(iso: string): string {
  if (iso.includes("T")) {
    const instant = new Date(iso);
    if (Number.isNaN(instant.getTime())) return iso;
    return instant.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }
  const [annee, mois, quantieme] = iso.split("-").map(Number);
  if (!annee || !mois || !quantieme) return iso;
  return new Date(annee, mois - 1, quantieme).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** La même, sans l'année : « mardi 2 septembre ». Pour les jours proches. */
export function enClairCourt(iso: string): string {
  const [annee, mois, quantieme] = iso.split("-").map(Number);
  if (!annee || !mois || !quantieme) return iso;
  return new Date(annee, mois - 1, quantieme).toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

/** Les noms que le français donne aux jours proches, et rien de plus. */
const NOMS: Record<number, string> = {
  [-1]: "hier",
  0: "aujourd'hui",
  1: "demain",
  2: "après-demain",
};

/**
 * Le nom relatif d'un jour proche, quand il en a un : « demain », « hier ».
 *
 * Hedi, le 15 septembre 2026, en demandant l'écran « Travail demandé » :
 * *« organisées par jour (demain, apres-demain, le jour d'après, … ) »*. Les
 * trois premiers sont des noms que le français porte ; « le jour d'après » n'en
 * est pas un — à J+3 il est ambigu (d'après quoi ?) et à J+9 il est faux. Au-delà
 * d'après-demain, c'est donc la date en clair qui nomme le jour, et
 * `enClairCourt` s'en charge.
 *
 * **Rend une chaîne vide plutôt qu'un nom forcé**, pour que l'appelant affiche
 * la date seule : « dans 9 jours » demanderait à un parent de compter pour
 * savoir de quel jour on parle.
 *
 * Le jour de référence vient du **serveur** et jamais de `new Date()` :
 * l'horloge d'une tablette se règle à la main, et c'est la décision prise le
 * 10 septembre 2026 pour l'heure d'ouverture du journal du soir. Une tablette
 * en retard d'un jour ferait lire « demain » sur un devoir dû aujourd'hui.
 */
export function nomRelatif(iso: string, aujourdhuiIso: string): string {
  const ecart = ecartEnJours(aujourdhuiIso, iso);
  return ecart === null ? "" : (NOMS[ecart] ?? "");
}

/**
 * Combien de jours séparent deux dates `YYYY-MM-DD`, ou `null` si l'une des deux
 * ne se lit pas.
 *
 * Les deux instants sont construits à midi et non à minuit : une heure de
 * décalage — celle que le passage à l'heure d'hiver ajoute entre deux dates de
 * part et d'autre du changement — ferait rendre 0,96 jour, et un « demain »
 * s'arrondirait en « aujourd'hui ».
 */
export function ecartEnJours(depuisIso: string, jusquAIso: string): number | null {
  const depuis = midi(depuisIso);
  const jusquA = midi(jusquAIso);
  if (depuis === null || jusquA === null) return null;
  return Math.round((jusquA - depuis) / 86_400_000);
}

function midi(iso: string): number | null {
  const [annee, mois, quantieme] = iso.split("-").map(Number);
  if (!annee || !mois || !quantieme) return null;
  return new Date(annee, mois - 1, quantieme, 12).getTime();
}
